import React from 'react';

function Privacy() {
  return (
    <section className="page-content">
      <div className="container">
        <h1 className="page-title">ForTuber Privacy Policy</h1>
        <div className="page-body legal-content">

          {/* Overview */}
          <div className="info-box">
            <p><strong>App Name:</strong> ForTuber</p>
            <p><strong>Developer:</strong> DIDI Connect</p>
            <p><strong>Company:</strong> DIDI</p>
          </div>

          <p>
            DIDI (hereinafter referred to as "the Company") is committed to protecting your personal information
            and handling any related complaints promptly and efficiently. This Privacy Policy outlines how we collect,
            use, store, and share your personal information when you use the ForTuber service.
          </p>

          <p>
            <strong>ForTuber uses the YouTube API Services.</strong> This service accesses and uses data provided by Google/YouTube in accordance with Google&apos;s policies. For Google&apos;s privacy practices, please see the <a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.
          </p>

          {/* Google API Services Compliance */}
          <div className="notice-box important">
            <p>
              <strong>Google API Services User Data Policy Compliance:</strong> ForTuber's use and transfer of information
              received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>,
              including the Limited Use requirements.
            </p>
          </div>

          {/* Use of Data Obtained Through Google APIs */}
          <h2>Use of Data Obtained Through Google APIs</h2>
          <p>
            <strong>Definition of &quot;Google user data&quot;:</strong> In this policy, &quot;Google user data&quot; means data we receive through Google APIs, including: your Google account information (email, name, profile picture), YouTube channel information (including channel description), video metadata (titles, descriptions, etc.), video subtitles/captions, and OAuth tokens. It does <strong>not</strong> include device-level advertising identifiers (ADID/IDFA) or general device/app usage data collected outside of Google APIs.
          </p>
          <p>
            <strong>How we use Google user data:</strong> We use Google user data <strong>only</strong> to (1) provide the app&apos;s core functionality (account sign-in, YouTube channel management including channel description and video captions, multi-language translation of channel description, video metadata, and subtitles), and (2) improve the app&apos;s user-facing features and performance. We do <strong>not</strong> use Google user data for: targeted, personalized, retargeted, or interest-based advertising; selling or sharing with data brokers or information resellers; training AI or machine learning models; determining creditworthiness or lending; or building user databases for purposes other than providing this app.
          </p>

          {/* 1. Changes to Privacy Policy */}
          <h2>1. Changes to Privacy Policy</h2>
          <p>
            The Company will notify users of any changes to this Privacy Policy at least 7 days before the changes
            take effect through in-app notifications and our website.
          </p>
          <ol>
            <li>For significant changes to personal information collection, usage purposes, or third-party sharing, we will provide individual notice via email or push notifications.</li>
            <li>Users have the right to disagree with the changes. If you do not agree, you may discontinue using the service and request account deletion.</li>
            <li>The revised Privacy Policy will take effect from the announced effective date.</li>
          </ol>

          {/* 2. Personal Information We Collect */}
          <h2>2. Personal Information We Collect</h2>

          <h3>1) Information We Collect</h3>
          <p>The Company collects the following personal information to provide our services. <strong>API Data</strong> (data we access, collect, store, and use through the YouTube API and Google APIs — including Google account information and YouTube channel/video data) is used only for app functionality; it is never used for advertising.</p>

          <p><strong>Data obtained through Google APIs (Google user data / API Data — used only for app functionality):</strong></p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Items Collected</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Required Information</strong></td>
                <td>Google account information (email, name, profile picture)</td>
                <td>Account registration, identity verification, service provision</td>
              </tr>
              <tr>
                <td><strong>YouTube Channel Data</strong></td>
                <td>Channel name, channel description, video metadata (titles, descriptions), video subtitles/captions</td>
                <td>Multi-language translation service</td>
              </tr>
            </tbody>
          </table>

          <p><strong>Other information (not Google user data):</strong></p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Items Collected</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Device Information</strong></td>
                <td>Device model, OS version, app version, unique device identifier</td>
                <td>Service optimization and technical support</td>
              </tr>
              <tr>
                <td><strong>Service Usage Records</strong></td>
                <td>Access logs, IP address, service usage history</td>
                <td>Service quality improvement and statistical analysis</td>
              </tr>
              <tr>
                <td><strong>Advertising Identifiers</strong></td>
                <td>ADID (Android), IDFA (iOS) — device-level only; not Google user data</td>
                <td>In-app ad delivery (Google user data is not used for advertising)</td>
              </tr>
            </tbody>
          </table>

          <h3>2) YouTube API Scopes We Request</h3>
          <p>ForTuber requests the following YouTube API permissions to provide our services:</p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Permission Scope</th>
                <th>Description</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>youtube</strong></td>
                <td>Manage your YouTube account</td>
                <td>Update channel description, video titles/descriptions, and video captions with translations</td>
              </tr>
              <tr>
                <td><strong>youtube.readonly</strong></td>
                <td>View your YouTube account</td>
                <td>Access channel description, video lists, and captions for translation</td>
              </tr>
              <tr>
                <td><strong>userinfo.profile</strong></td>
                <td>Google Sign-In authentication</td>
                <td>Account authentication</td>
              </tr>
              <tr>
                <td><strong>userinfo.email</strong></td>
                <td>Google Sign-In authentication</td>
                <td>Account authentication</td>
              </tr>
            </tbody>
          </table>

          <h3>3) How We Collect Information</h3>
          <ol>
            <li><strong>During Registration:</strong> Account information is collected through Google Sign-In (Firebase Authentication).</li>
            <li><strong>During Service Use:</strong> Channel description, video metadata, and video captions are collected through the YouTube API.</li>
            <li><strong>Automatic Collection:</strong> Device information and service usage records are automatically generated and collected during app use.</li>
            <li><strong>Customer Inquiries:</strong> Additional information may be collected through email or customer support.</li>
          </ol>

          {/* 3. How We Use Your Information */}
          <h2>3. How We Use Your Information</h2>
          <p>The Company uses collected personal information only for the following purposes:</p>

          <h3>1) Use of Google User Data</h3>
          <p>Data we receive through Google APIs (Google account information, YouTube channel and video data including channel description and captions) is used <strong>only</strong> to provide and improve the app&apos;s core features: account authentication, YouTube channel management, and multi-language translation of channel description, video metadata, and subtitles. We do not use Google user data for advertising, analytics for ad targeting, or any purpose other than providing or improving this application&apos;s functionality.</p>
          <p><strong>How we use, process, and share this information:</strong> We process YouTube API data and Google user data only internally to operate the Service (e.g., translation, channel management). We do not share this data with external parties for advertising or marketing. We share such data only with Google/Firebase as necessary for authentication and API operation, and we do not sell or transfer it to other third parties. See Section 5 for details on third-party sharing.</p>

          <h3>2) Service Provision</h3>
          <ol>
            <li><strong>Multi-Language Translation:</strong> Using AI technology to automatically translate channel description, video titles and descriptions (metadata), and video subtitles/captions into multiple languages, helping creators reach global audiences.</li>
            <li><strong>Member Management:</strong> Verifying membership registration intent, identity verification, and preventing unauthorized use.</li>
          </ol>

          <h3>3) Notification Services</h3>
          <ol>
            <li>Delivering important service announcements</li>
            <li>Notifying about service updates and new features</li>
            <li>Sending notifications about translation completion and other service activities</li>
          </ol>

          <h3>4) Service Quality Improvement</h3>
          <ol>
            <li>Enhancing service features and improving user experience</li>
            <li>Analyzing service usage statistics</li>
            <li>Developing new features and providing personalized services</li>
          </ol>

          {/* 4. How We Store Your Information */}
          <h2>4. How We Store Your Information</h2>

          <h3>1) Storage Location and Methods</h3>
          <ol>
            <li><strong>Firebase (Google Cloud):</strong> Account information and service usage data are encrypted and securely stored on Firebase.</li>
            <li><strong>Local Storage (User's Device):</strong> YouTube channel authorization tokens (Access Token, Refresh Token) are encrypted and stored only on the user's device. These tokens are never transmitted to external servers or shared with third parties.</li>
          </ol>

          <h3>2) Information Stored or Accessed on Your Device</h3>
          <p>The Company and our service providers may store, access, or collect information on or from your device (including via local storage, app data, and similar technology). We may also use cookies or similar technologies (e.g., local storage, session storage) where the Service is provided through a browser. Third-party services we use (such as Google AdMob or analytics) may place or recognize cookies or similar technology on your device or browser. This is used for authentication, service operation, and (where applicable) non–Google-user-data advertising. You can control cookie and advertising preferences through your device or browser settings.</p>

          <h3>3) Retention Period</h3>
          <p>Personal information is destroyed without delay once the purpose of collection and use has been achieved. However, information may be retained for the following periods as required by applicable laws:</p>
          <ol>
            <li>Records regarding contracts or withdrawal of subscription: 5 years (Consumer Protection Laws)</li>
            <li>Records regarding payment and supply of goods: 5 years (Consumer Protection Laws)</li>
            <li>Records regarding consumer complaints or dispute resolution: 3 years (Consumer Protection Laws)</li>
            <li>Service access records: 3 months (Communications Privacy Laws)</li>
          </ol>

          {/* 5. Third-Party Sharing */}
          <h2>5. Third-Party Sharing</h2>
          <p>
            The Company processes personal information only within the scope specified in this Privacy Policy and
            does not provide it to third parties without user consent. However, the following exceptions apply:
          </p>

          <div className="notice-box important">
            <p>
              <strong>Google user data is not shared for advertising:</strong> We do not share, transfer, or disclose data obtained through Google APIs (Google account information, YouTube channel or video data) with AdMob, analytics providers for ad targeting, data brokers, or any third party for advertising purposes. Google user data is used only for providing and improving this app&apos;s functionality.
            </p>
          </div>

          <h3>1) Third-Party Service Providers</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Data Shared</th>
                <th>Purpose</th>
                <th>Retention Period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google AdMob</td>
                <td>Device-level advertising identifiers (ADID/IDFA), device information only. Google user data is not shared.</td>
                <td>In-app ad delivery (no use of Google user data for advertising)</td>
                <td>Until purpose is achieved</td>
              </tr>
              <tr>
                <td>Google Analytics</td>
                <td>Service usage records, device information (no Google account or YouTube data used for ad targeting)</td>
                <td>Service usage analysis and quality improvement</td>
                <td>Until purpose is achieved</td>
              </tr>
              <tr>
                <td>Firebase (Google)</td>
                <td>Account information, service data</td>
                <td>Google Sign-In authentication, data storage</td>
                <td>Until account deletion</td>
              </tr>
            </tbody>
          </table>

          <h3>2) Token Information Protection</h3>
          <div className="notice-box">
            <p>
              <strong>Important:</strong> YouTube channel authorization tokens (Access Token and Refresh Token) are
              encrypted and stored only on the user's device. These tokens are never transmitted to external servers
              or shared with any third parties. They are used solely for service provision within the user's device.
            </p>
          </div>

          <h3>3) Disclosure Required by Law</h3>
          <p>Personal information may be provided when required by law or when requested by investigative agencies following legal procedures.</p>

          {/* 6. Security Measures */}
          <h2>6. Security Measures</h2>
          <p>The Company implements the following security measures to protect user personal information:</p>

          <h3>1) Encryption</h3>
          <ol>
            <li><strong>Data Transmission Encryption:</strong> All data transmissions are encrypted using TLS/SSL protocols.</li>
            <li><strong>Data Storage Encryption:</strong> Sensitive personal information is encrypted using strong encryption algorithms such as AES.</li>
            <li><strong>Token Encryption:</strong> YouTube Access Token and Refresh Token are encrypted when stored locally.</li>
          </ol>

          <h3>2) Access Control</h3>
          <ol>
            <li>Personnel handling personal information are minimized and granted differentiated access rights.</li>
            <li>Access records to personal information processing systems are maintained and regularly reviewed.</li>
            <li>Intrusion detection/prevention systems are operated to block unauthorized access.</li>
          </ol>

          <h3>3) Security Procedures</h3>
          <ol>
            <li>Regular security inspections and vulnerability assessments are conducted.</li>
            <li>Security updates are continuously applied to maintain the latest security status.</li>
            <li>Regular security training is provided to personnel handling personal information.</li>
          </ol>

          {/* 7. Data Retention and Deletion */}
          <h2>7. Data Retention and Deletion</h2>

          <h3>1) Storage</h3>
          <ol>
            <li>User personal information is securely stored through Firebase (Google Cloud).</li>
            <li>Mandatory retention periods required by applicable laws are observed.</li>
          </ol>

          <h3>2) How to Request Deletion of Stored Data and Revoke Access</h3>
          <p>You can delete the data we store and revoke ForTuber&apos;s access to your Google/YouTube data in the following ways:</p>
          <div className="notice-box">
            <p>
              <strong>1) Delete your ForTuber account (app-side):</strong> You can delete your account and associated stored data directly through the app by navigating to <strong>Settings → Delete Account</strong>. Upon account deletion, all personal information we hold will be deleted after any legally required retention periods have ended. You can also request account deletion via email at cddchingudeul@gmail.com.
            </p>
            <p>
              <strong>2) Revoke ForTuber&apos;s access in your Google account:</strong> You may revoke this app&apos;s access to your Google/YouTube data at any time through your Google account settings. This stops ForTuber from accessing your authorized data even if you do not delete your ForTuber account. To revoke access, go to the Google account &quot;Third-party apps with account access&quot; (or &quot;Connections&quot;) page: <a href="https://myaccount.google.com/connections?filters=3,4&hl=en" target="_blank" rel="noopener noreferrer">https://myaccount.google.com/connections?filters=3,4&amp;hl=en</a>, find ForTuber (or DIDI), and remove access.
            </p>
          </div>
          <ol>
            <li>After you delete your account or revoke access, we will no longer use your Google/YouTube data for the Service. Data already stored will be handled in accordance with this policy (deleted upon account deletion, subject to legal retention where applicable).</li>
          </ol>

          <h3>3) Automatic Deletion Criteria</h3>
          <ol>
            <li>Personal information is deleted immediately upon account withdrawal, except for information with legal retention requirements, which is separately stored and deleted after the retention period.</li>
            <li>Accounts inactive for 1 year will be converted to dormant accounts, and after separate notification, personal information may be separately stored or deleted.</li>
          </ol>

          <h3>4) Destruction Methods</h3>
          <ol>
            <li>Electronic files: Permanently deleted using methods that make recovery impossible.</li>
            <li>Paper documents: Shredded or incinerated.</li>
          </ol>

          {/* 8. What We Do NOT Use Your Data For */}
          <h2>8. What We Do NOT Use Your Data For</h2>
          <div className="notice-box important">
            <p><strong>The Company will NEVER use your personal information for the following purposes:</strong></p>
          </div>
          <p>
            <strong>Google user data</strong> (data obtained through Google APIs) is not used for: targeted, personalized, retargeted, or interest-based advertising; selling or providing to data brokers or information resellers; training AI or machine learning models; determining creditworthiness or lending; or building user databases for purposes other than providing this app.
          </p>
          <ol>
            <li><strong>Data Sales:</strong> We do not sell your personal information to third parties.</li>
            <li><strong>Third-Party Sales for Advertising Profiling:</strong> We do not sell personal information externally for advertising purposes.</li>
            <li><strong>AI/Machine Learning Training:</strong> We do not use your personal information to train AI or machine learning models.</li>
            <li><strong>Credit Evaluation:</strong> We do not use personal information for credit evaluation or financial purposes.</li>
            <li><strong>Other Purposes:</strong> We do not use your data for any purposes other than those specified in this Privacy Policy for service provision.</li>
          </ol>
          <p>
            In accordance with the Google API Services User Data Policy, ForTuber limits its use of data obtained through
            Google APIs to providing and improving user-facing features that are prominent in the requesting application&apos;s user interface. We do not use Google user data for any other purpose.
          </p>

          {/* 9. User Rights */}
          <h2>9. User Rights</h2>
          <p>Users may exercise the following rights at any time:</p>
          <ol>
            <li><strong>Right to Access:</strong> You can view the status of your personal information processing.</li>
            <li><strong>Right to Rectification/Deletion:</strong> You can request correction or deletion of errors in your personal information.</li>
            <li><strong>Right to Suspend Processing:</strong> You can request suspension of personal information processing.</li>
            <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent to the collection and use of personal information.</li>
          </ol>
          <p>
            These rights can be exercised through the app's settings menu or via email at cddchingudeul@gmail.com.
            The Company will take necessary action without delay.
          </p>

          {/* 10. Automatic Information Collection */}
          <h2>10. Automatic Information Collection</h2>

          <h3>1) Information Automatically Collected</h3>
          <p>The following information may be automatically generated and collected during service use:</p>
          <ol>
            <li>Device Information: Device model, OS version, unique device identifier, mobile network information</li>
            <li>Advertising Identifiers: ADID (Android), IDFA (iOS)</li>
            <li>Service Usage Records: Access logs, IP address, access date and time</li>
          </ol>

          <h3>2) How to Opt Out of Advertising Identifier Collection</h3>
          <ol>
            <li><strong>Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization</li>
            <li><strong>iOS:</strong> Settings → Privacy → Tracking → Turn off "Allow Apps to Request to Track"</li>
          </ol>
          <p>Opting out of advertising identifier collection will not restrict service use, but personalized advertising may be limited.</p>

          {/* 11. Privacy Officer */}
          <h2>11. Privacy Officer</h2>
          <p>
            The Company designates a Privacy Officer to oversee personal information processing and handle
            user complaints and damage relief as follows:
          </p>

          <div className="contact-box">
            <strong>Privacy Officer</strong>
            <p>
              Company: DIDI<br />
              Developer: DIDI Connect<br />
              App Name: ForTuber<br />
              Name: Sangmin Choi<br />
              Position: CEO &amp; Manager<br />
              Email: cddchingudeul@gmail.com
            </p>
          </div>

          {/* 12. Remedies for Rights Infringement */}
          <h2>12. Remedies for Rights Infringement</h2>
          <p>
            Users can contact the following organizations for reporting or consulting on personal information infringement:
          </p>
          <ol>
            <li>Personal Information Infringement Report Center (privacy.kisa.or.kr / 118)</li>
            <li>Supreme Prosecutors' Office Cyber Investigation Department (www.spo.go.kr / 1301)</li>
            <li>National Police Agency Cyber Bureau (cyberbureau.police.go.kr / 182)</li>
          </ol>

          {/* 13. YouTube and Google Brand Guidelines */}
          <h2>13. YouTube and Google Brand Guidelines</h2>
          <p>
            ForTuber uses the YouTube API Services and complies with the following guidelines and policies:
          </p>
          <ol>
            <li><a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a></li>
            <li><a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
            <li><a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a></li>
            <li><a href="https://about.google/brand-resource-center/" target="_blank" rel="noopener noreferrer">Google Brand Guidelines</a></li>
          </ol>
          <p>
            Users can revoke ForTuber&apos;s access to their data at any time via the Google account connections page: <a href="https://myaccount.google.com/connections?filters=3,4&hl=en" target="_blank" rel="noopener noreferrer">https://myaccount.google.com/connections?filters=3,4&amp;hl=en</a>.
          </p>

          <p className="effective-date">This policy is effective from January 19, 2026.</p>
        </div>
      </div>
    </section>
  );
}

export default Privacy;
